import Modifier from 'ember-modifier'
import { service } from '@ember/service'
import type AnalyticsService from 'teacher-ui-client/services/analytics'
import type TeacherEvents from 'teacher-ui-client/services/teacher-events'
import type { SubscriptionTypeService } from 'teacher-ui-client/services/subscription-type'

export const PDF_DOWNLOAD_EVENT_NAME = 'pdf-download'

/**
 * Fallback product where no product is set (case studies, research reports, teacher guides and similar).
 */
export const PRODUCT_NOT_APPLICABLE = 'teacher-ui'

export interface PdfDownloadEventData {
  /** Broad grouping of the download, e.g. 'Printables Library'. */
  category: string
  /** Human readable name of the resource, usually the Contentful entry title. */
  label?: string
  /** The Contentful asset file name. */
  file?: string
  /** Printables PDF variant, e.g. 'teacherNotes'. */
  pdfType?: string
  /** Book code, for book library and printables downloads. */
  bookCode?: string
  /** Viewer role, for book library downloads. */
  role?: string
  /** What triggered the download, e.g. 'modal' or 'dropdown'. */
  via?: string
  /** Lesson resource type, for lesson preview downloads. */
  resourceType?: string
}

interface Signature {
  Args: {
    Named: PdfDownloadEventData & {
      product?: string
    }
    Positional: []
  }
  Element: HTMLElement
}

/**
 * Modifier: pdfDownloadEvent
 *
 * Records a PDF download in PostHog *and* Teacher Totter
 *
 * Usage (in template):
 *   <a {{pdfDownloadEvent category="Teacher Guide PDF" label=@title}}>Download</a>
 *
 */
export class pdfDownloadEvent extends Modifier<Signature> {
  @service analytics!: AnalyticsService

  @service teacherEvents!: TeacherEvents

  @service subscriptionType!: SubscriptionTypeService

  didSetup = false

  modify(
    element: Signature['Element'],
    _positional: Signature['Args']['Positional'],
    named: Signature['Args']['Named'],
  ) {
    if (this.didSetup) return
    this.didSetup = true

    element.addEventListener('click', () => {
      const { product, ...eventData } = named

      sendPdfDownloadEvent(
        {
          analytics: this.analytics,
          teacherEvents: this.teacherEvents,
          subscriptionType: this.subscriptionType,
        },
        eventData,
        product,
      )
    })
  }
}

export interface PdfDownloadEventServices {
  analytics: AnalyticsService
  teacherEvents: TeacherEvents
  subscriptionType?: SubscriptionTypeService
}

/**
 * Sends a PDF download event to PostHog and Teacher Totter with identical
 * metadata.
 *
 */
export function sendPdfDownloadEvent(
  services: PdfDownloadEventServices,
  eventData: PdfDownloadEventData,
  product?: string,
): void {
  const { analytics, teacherEvents, subscriptionType } = services

  // both services no-op internally when disabled (posthog.enabled / teacher_totter flag)
  analytics.capture(PDF_DOWNLOAD_EVENT_NAME, eventData)

  teacherEvents.sendEvent(
    PDF_DOWNLOAD_EVENT_NAME,
    product ?? subscriptionType?.currentValue ?? PRODUCT_NOT_APPLICABLE,
    {
      ...eventData,
    },
  )
}
